// src/modules/app-views/omnichannel/pages/Init/components/MediaPreviewModal.jsx
// *******************************************************************************
// MEDIA PREVIEW MODAL — Fullscreen image/video/document preview overlay
// *******************************************************************************

import { useState, useCallback, useEffect, useMemo } from 'react';
import { Modal, Spin } from 'antd';
import { FilePdfOutlined } from '@ant-design/icons';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure pdf.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Minimum time to show loading state (prevents flash)
const MIN_LOADING_MS = 1200;

// *******************************************************************************
// COMPONENT
// *******************************************************************************

const MediaPreviewModal = ({ open, onClose, mediaUrl, mediaType, caption }) => {
  const [numPages, setNumPages] = useState(null);
  const [ready, setReady] = useState(false);

  const isVideo = mediaType === 'video';
  const isDocument = mediaType === 'document';

  // Stable file URL — only changes when modal opens or mediaUrl changes (prevents re-fetch on state updates)
  const fileUrl = useMemo(() => {
    if (!open || !isDocument || !mediaUrl) return null;
    return mediaUrl + (mediaUrl.includes('?') ? '&' : '?') + '_t=' + Date.now();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mediaUrl, isDocument]);

  // Reset ready state when modal opens
  useEffect(() => {
    if (!open) {
      setReady(false);
      setNumPages(null);
    }
  }, [open]);

  // Remove any auto focus when modal opens
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        const el = document.activeElement;
        if (el) el.blur();
      }, 0);
    }
  }, [open]);

  // Force remove focus from close button (Antd v6 workaround)
  useEffect(() => {
    if (open) {
      const btn = document.querySelector('.ant-modal-close');
      if (btn) btn.setAttribute('tabindex', '-1');
    }
  }, [open]);

  const onDocumentLoadSuccess = useCallback(({ numPages: total }) => {
    setNumPages(total);
    // Ensure loading shows for at least MIN_LOADING_MS
    setTimeout(() => setReady(true), MIN_LOADING_MS);
  }, []);

  if (!mediaUrl) return null;

  return (
    <Modal
      open={open}
      onCancel={() => {
        onClose();
        setTimeout(() => {
          const el = document.activeElement;
          if (el) el.blur();
        }, 0);
      }}
      footer={null}
      closable={isDocument}
      autoFocus={false}
      modalRender={node => <div tabIndex={-1}>{node}</div>}
      title={
        isDocument ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FilePdfOutlined style={{ color: '#ff4d4f' }} />
            Visualização PDF
          </span>
        ) : null
      }
      centered
      width={isDocument ? 850 : 'auto'}
      destroyOnHidden
      styles={{
        body: {
          padding: 0,
          lineHeight: isDocument ? 'normal' : 0,
          textAlign: 'center',
          maxHeight: isDocument ? '80vh' : 'auto',
          overflowY: isDocument ? 'auto' : 'visible',
        },
        content: {
          padding: isDocument ? undefined : 0,
          background: isDocument ? undefined : 'transparent',
          boxShadow: isDocument ? undefined : 'none',
          borderRadius: 8,
          overflow: 'hidden',
        },
      }}
    >
      {isDocument ? (
        <>
          {/* Loading overlay — visible until PDF is fully loaded */}
          {!ready && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: 400,
                gap: 12,
              }}
            >
              <Spin size="large" />
              <span style={{ opacity: 0.5, fontSize: 13 }}>Carregando documento...</span>
            </div>
          )}

          {/* PDF rendered hidden until ready, then shown */}
          <div style={{ display: ready ? 'block' : 'none', background: '#525659' }}>
            <Document
              file={fileUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              loading=""
              error={
                <div style={{ padding: 40, color: '#fff', textAlign: 'center' }}>
                  Não foi possível carregar o documento.
                </div>
              }
            >
              {numPages &&
                Array.from({ length: numPages }, (_, i) => (
                  <Page
                    key={i + 1}
                    pageNumber={i + 1}
                    width={800}
                    renderTextLayer
                    renderAnnotationLayer
                  />
                ))}
            </Document>
          </div>
        </>
      ) : isVideo ? (
        <video
          src={mediaUrl}
          controls
          autoPlay
          playsInline
          style={{ maxWidth: '85vw', maxHeight: '80vh', borderRadius: 8 }}
        >
          <source src={mediaUrl} type="video/mp4" />
        </video>
      ) : (
        <img
          src={mediaUrl}
          alt={caption || 'preview'}
          style={{ maxWidth: '85vw', maxHeight: '80vh', borderRadius: 8 }}
        />
      )}
      {caption && !isDocument && (
        <div
          style={{
            color: '#fff',
            textAlign: 'center',
            padding: '8px 12px',
            fontSize: 13,
          }}
        >
          {caption}
        </div>
      )}
    </Modal>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************

export default MediaPreviewModal;
